/**
 * 阿里云验证码2.0 Hook
 * prefix: 1w9f1a  SceneId: 1orrwmn2
 *
 * verify(bizFn) 接收业务函数，在 captchaVerifyCallback 内调用。
 * code=0 → captchaResult: true → SDK 展示成功 → onBizResultCallback resolve
 * code=600022 → captchaResult: false → SDK 展示"验证失败"并自动重载验证码
 */
import { useRef, useCallback } from 'react';

const CAPTCHA_SDK = 'https://o.alicdn.com/captcha-frontend/aliyunCaptcha/AliyunCaptcha.js';

export function useAliyunCaptcha() {
  const captchaRef = useRef<any>(null);
  const resolveRef = useRef<((data: any) => void) | null>(null);
  const rejectRef = useRef<((e: any) => void) | null>(null);
  const pendingResultRef = useRef<any>(null);
  const bizFnRef = useRef<((param: string) => Promise<any>) | null>(null);

  const loadSdk = useCallback((): Promise<void> => {
    if (document.querySelector(`script[src="${CAPTCHA_SDK}"]`)) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = CAPTCHA_SDK;
      script.onload = () => resolve();
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }, []);

  const initCaptcha = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      const containerId = 'aliyun-captcha-container';
      if (!document.getElementById(containerId)) {
        const div = document.createElement('div');
        div.id = containerId;
        document.body.appendChild(div);
      }

      (window as any).initAliyunCaptcha({
        SceneId: '1orrwmn2',
        prefix: '1w9f1a',
        mode: 'popup',
        element: `#${containerId}`,
        button: `#${containerId}`,
        captchaVerifyCallback: async (captchaVerifyParam: string) => {
          if (!bizFnRef.current) {
            return { captchaResult: true, bizResult: true };
          }
          try {
            const res = await bizFnRef.current(captchaVerifyParam);
            if (res?.code === 0) {
              pendingResultRef.current = res;
              return { captchaResult: true, bizResult: true };
            }
            // 600022 等验证失败：captchaResult: false
            // SDK 显示"验证失败"并自动重新加载验证码，不清 refs，用户重试后仍能 resolve
            return { captchaResult: false };
          } catch {
            return { captchaResult: false };
          }
        },
        onBizResultCallback: () => {
          if (resolveRef.current && pendingResultRef.current !== null) {
            resolveRef.current(pendingResultRef.current);
            resolveRef.current = null;
            rejectRef.current = null;
            pendingResultRef.current = null;
          }
        },
        onClose: () => {
          captchaRef.current = null;
          if (rejectRef.current) {
            rejectRef.current(new Error('user_cancel'));
            resolveRef.current = null;
            rejectRef.current = null;
            pendingResultRef.current = null;
          }
        },
        getInstance: (instance: any) => {
          captchaRef.current = instance;
          resolve();
        },
        slideStyle: { width: 360, height: 40 },
        language: 'cn',
      });

      setTimeout(() => {
        if (!captchaRef.current) reject(new Error('验证码初始化超时'));
      }, 5000);
    });
  }, []);

  const verify = useCallback((bizFn: (param: string) => Promise<any>): Promise<any> => {
    return new Promise(async (resolve, reject) => {
      try {
        await loadSdk();
        bizFnRef.current = bizFn;
        resolveRef.current = resolve;
        rejectRef.current = reject;
        pendingResultRef.current = null;

        if (!captchaRef.current) {
          await initCaptcha();
        }

        captchaRef.current.show();
      } catch (e) {
        reject(e);
      }
    });
  }, [loadSdk, initCaptcha]);

  return { verify };
}
